import 'shop/SlerpStyles'

import { useTheme } from 'shop/theme'

import ShopClient from 'shop/client'

import { Router } from './router'
import { DevInfoBanner } from 'shop/components/DevInfoBanner'
import GlobalContext from 'shop/hooks/useGlobalContext/GlobalContext'
import config from 'shop/config'
import { initSentry } from 'reporting/sentry'
import { useEffect, useMemo } from 'react'
import { initializeSlerpGA4 } from 'tracker'
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react'
import { autoAttributesPlugin } from '@growthbook/growthbook/plugins'
import { trackGA4GrowthbookExperimentViewed } from 'tracker/GA/plugins'
import { useLocation } from 'react-router-dom'

const SlerpSpace = () => {
  const [theme] = useTheme((config.theme && config.theme) || {})
  const location = useLocation()

  const isDevEnv: boolean = !!process.env.REACT_APP_TEST_ENV
  const environment = process.env.REACT_APP_TEST_ENV || process.env.NODE_ENV
  const isTestEnv: boolean = isDevEnv || environment === 'development'

  initSentry({
    environment: environment
  })

  if (config.isAnalyticsEnabled) {
    // Init SLERP GA4 Tracker on load, expand it's data once Partner query resolves
    initializeSlerpGA4('', {
      merchant_id: config.partnerId,
      merchant_slug: config.domain
    })
  }

  const gb = useMemo(
    () =>
      new GrowthBook({
        apiHost: 'https://cdn.growthbook.io',
        clientKey: process.env.REACT_APP_GROWTHBOOK_KEY,
        enableDevMode: isTestEnv,
        trackingCallback: (experiment, result) => {
          // Exception for GB feature, "landing_page_default_to_browse", that defaults Landing page to Browse set within useShop.
          // Only track and enrich data if we are on the Landing page to actually "view it".
          if (
            result.featureId === 'landing_page_default_to_browse' &&
            location.pathname !== '/'
          ) {
            return
          }
          trackGA4GrowthbookExperimentViewed({
            experimentId: experiment.key,
            variationId: result.key
          })
        },
        plugins: [autoAttributesPlugin()]
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTestEnv]
  )

  /** Temp: Switch pathname for domain masked partners if they haven't updated yet */
  useEffect(() => {
    if (window.location.hostname === 'www.crosstown.co.uk') {
      const newHostName = 'order.crosstown.co.uk'
      window.location.href = `https://${newHostName}${window.location.pathname}${window.location.search}`
    }
    if (window.location.hostname === 'eataly.co.uk') {
      const newHostName = 'eataly.slerp.com'
      window.location.href = `https://${newHostName}${window.location.pathname}${window.location.search}`
    }
  }, [])

  useEffect(() => {
    // "changes to features within GrowthBook will be streamed to the SDK"
    gb.init({ streaming: true })
  }, [gb])

  return (
    <GrowthBookProvider growthbook={gb}>
      <ShopClient config={config}>
        <GlobalContext theme={theme}>
          {isDevEnv && <DevInfoBanner />}
          <Router />
        </GlobalContext>
      </ShopClient>
    </GrowthBookProvider>
  )
}

export default SlerpSpace
